import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Pressable, Text, View } from 'react-native'

import { buildMediaErrorExtra } from '../../utils/media-error'
import { addBreadcrumb, captureMessage } from '../../utils/sentry'
import { Icon, symbolName } from '../ui/Icon'

const AUDIO_PLAYER_ACCENT = '#FFB104'
const AUDIO_PLAYER_ICON = '#FFFFFF'

interface AudioPlayerProps {
  source: { uri: string }
  autoPlay?: boolean
  onLoadingChange?: (isLoading: boolean) => void
  onErrorChange?: (hasError: boolean) => void
  ref?: React.Ref<AudioPlayerHandle>
}

export interface AudioPlayerHandle {
  pause: () => void
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00'
  const totalSeconds = Math.floor(seconds)
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function AudioPlayer({
  source,
  autoPlay = true,
  onLoadingChange,
  onErrorChange,
  ref,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const hasResolvedInitialLoadRef = useRef(false)
  const lastReportedErrorSignatureRef = useRef<string | null>(null)
  const onLoadingChangeRef = useRef(onLoadingChange)
  const onErrorChangeRef = useRef(onErrorChange)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange
  }, [onLoadingChange])

  useEffect(() => {
    onErrorChangeRef.current = onErrorChange
  }, [onErrorChange])

  const emitLoadingChange = useCallback((isLoading: boolean) => {
    onLoadingChangeRef.current?.(isLoading)
  }, [])
  const emitErrorChange = useCallback((hasError: boolean) => {
    onErrorChangeRef.current?.(hasError)
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      pause() {
        audioRef.current?.pause()
      },
    }),
    [],
  )

  const resolveInitialLoading = useCallback(() => {
    if (hasResolvedInitialLoadRef.current) return
    hasResolvedInitialLoadRef.current = true
    emitLoadingChange(false)
  }, [emitLoadingChange])

  const reportError = useCallback(
    (reason: string, rawError?: unknown) => {
      const diagnostics: Record<string, unknown> = {
        sourceUri: source.uri,
        reason,
        ...buildMediaErrorExtra(rawError),
      }
      const signature = JSON.stringify({
        reason,
        rawErrorMessage: diagnostics.rawErrorMessage ?? null,
        sourceUri: source.uri,
      })

      if (lastReportedErrorSignatureRef.current === signature) return
      lastReportedErrorSignatureRef.current = signature

      console.error('[AudioPlayer] Playback failed', diagnostics)
      captureMessage('Audio playback failed', 'error', {
        feature: 'media',
        tags: { player: 'audio-web', reason },
        extra: diagnostics,
      })
    },
    [source.uri],
  )

  useEffect(() => {
    hasResolvedInitialLoadRef.current = false
    lastReportedErrorSignatureRef.current = null
    emitLoadingChange(true)
    emitErrorChange(false)
    addBreadcrumb('media', 'audio_load_start', { sourceUri: source.uri })
  }, [emitErrorChange, emitLoadingChange, source.uri])

  // Auto-play on mount
  useEffect(() => {
    const audio = audioRef.current
    if (audio == null) return

    if (autoPlay) {
      audio.play().catch(() => {
        // Autoplay may be blocked by browser policy; user can tap play.
        setIsPlaying(false)
      })
    }
  }, [autoPlay, source.uri])

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (audio == null) return
    setCurrentTime(audio.currentTime)
    if (
      audio.currentTime > 0 ||
      audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
    ) {
      resolveInitialLoading()
    }
  }, [resolveInitialLoading])

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current
    if (audio == null) return
    setDuration(audio.duration)
    resolveInitialLoading()
  }, [resolveInitialLoading])

  const handleLoadedData = useCallback(() => {
    resolveInitialLoading()
  }, [resolveInitialLoading])

  const handleLoadStart = useCallback(() => {
    if (!hasResolvedInitialLoadRef.current) {
      emitLoadingChange(true)
    }
  }, [emitLoadingChange])

  const handleCanPlay = useCallback(() => {
    resolveInitialLoading()
  }, [resolveInitialLoading])

  const handleCanPlayThrough = useCallback(() => {
    resolveInitialLoading()
  }, [resolveInitialLoading])

  const handleProgress = useCallback(() => {
    const audio = audioRef.current
    if (audio == null) return
    if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      resolveInitialLoading()
    }
  }, [resolveInitialLoading])

  const handlePlaying = useCallback(() => {
    resolveInitialLoading()
    emitErrorChange(false)
    setIsPlaying(true)
    addBreadcrumb('media', 'audio_ready', { sourceUri: source.uri })
  }, [emitErrorChange, resolveInitialLoading, source.uri])

  const handleSeeked = useCallback(() => {
    resolveInitialLoading()
  }, [resolveInitialLoading])

  const handleError = useCallback(() => {
    emitLoadingChange(false)
    emitErrorChange(true)
    addBreadcrumb('media', 'audio_error', { sourceUri: source.uri })
    reportError('web_error')
  }, [emitErrorChange, emitLoadingChange, reportError, source.uri])

  const handlePlay = useCallback(() => setIsPlaying(true), [])
  const handlePause = useCallback(() => {
    resolveInitialLoading()
    setIsPlaying(false)
  }, [resolveInitialLoading])
  const handleEnded = useCallback(() => {
    resolveInitialLoading()
    setIsPlaying(false)
  }, [resolveInitialLoading])

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (audio == null) return

    if (!audio.paused) {
      audio.pause()
    } else {
      audio.play().catch(() => {
        emitLoadingChange(false)
        emitErrorChange(true)
        setIsPlaying(false)
        reportError('play_rejected')
      })
    }
  }, [emitErrorChange, emitLoadingChange, reportError])

  const handleScrub = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (audio == null) return
    const time = parseFloat(e.target.value)
    audio.currentTime = time
    setCurrentTime(time)
  }, [])

  const progress = duration > 0 ? currentTime / duration : 0

  return (
    <View className="w-full px-4 py-4">
      {/* Hidden HTML5 audio element */}
      <audio
        ref={audioRef}
        src={source.uri}
        preload="auto"
        onLoadStart={handleLoadStart}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onCanPlay={handleCanPlay}
        onCanPlayThrough={handleCanPlayThrough}
        onProgress={handleProgress}
        onPlaying={handlePlaying}
        onSeeked={handleSeeked}
        onError={handleError}
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      />

      <View className="flex-row items-center">
        <Text className="w-12 text-center text-xs font-medium text-gray-500">
          {formatTime(currentTime)}
        </Text>

        <View className="mx-3 flex-1" style={{ paddingVertical: 4 }}>
          <input
            className="audio-player-range"
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={currentTime}
            onChange={handleScrub}
            disabled={duration === 0}
            aria-label="Audio progress"
            style={scrubberStyle(progress)}
          />
        </View>

        <Text className="w-12 text-center text-xs font-medium text-gray-500">
          {formatTime(duration)}
        </Text>
      </View>

      <View className="mt-2 items-center">
        <Pressable
          onPress={togglePlayPause}
          className="h-[40px] w-[40px] items-center justify-center rounded-full"
          style={{ backgroundColor: AUDIO_PLAYER_ACCENT }}
          accessibilityRole="button"
          accessibilityLabel={isPlaying ? 'Pause' : 'Play'}
        >
          <Icon
            name={
              isPlaying
                ? symbolName('pause.fill', 'pause')
                : symbolName('play.fill', 'play_arrow')
            }
            size={24}
            color={AUDIO_PLAYER_ICON}
          />
        </Pressable>
      </View>
    </View>
  )
}

function scrubberStyle(progress: number): React.CSSProperties {
  const percent = `${Math.round(progress * 100)}%`
  return {
    WebkitAppearance: 'none',
    appearance: 'none',
    accentColor: AUDIO_PLAYER_ACCENT,
    width: '100%',
    height: 4,
    borderRadius: 2,
    outline: 'none',
    cursor: 'pointer',
    background: `linear-gradient(to right, ${AUDIO_PLAYER_ACCENT} ${percent}, #E5E7EB ${percent})`,
  }
}
